import React, { useEffect, useState } from 'react';
import { QueryObserver, useQueryClient } from '@tanstack/react-query';
import { OrdersCallback, OrdersContext } from '../contexs/orders-context';
import { useCartsMutations } from '../api/hooks/vendor/carts/use-carts-mutations';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { usePrintService } from '../hooks/use-print-service.hook';

interface OrdersCallbackWithId {
  id: string;
  callback: OrdersCallback;
}

interface OrdersProviderProps {
  children: React.ReactNode;
}

// Provider to manage new order notifications
export const OrdersProvider = (props: OrdersProviderProps) => {
  const queryClient = useQueryClient();
  const { setOrderReceived, printReceipt } = useCartsMutations();
  const printService = usePrintService();
  // States
  const [callbacks, setCallbacks] = useState<OrdersCallbackWithId[]>([]);

  // Function to allow child components to register callbacks for new orders
  const registerCallback = (id: string, callback: OrdersCallback) => {
    setCallbacks(prev => {
      const isCallbackRegistered = prev.some(cb => cb.id === id);

      if (!isCallbackRegistered) {
        return [...prev, { id, callback }];
      }
      return prev; // No changes if already registered
    });

    return () => {
      setCallbacks(prev => prev.filter(cb => cb.id !== id));
    };
  };

  useEffect(() => {
    // Create a React Query observer for monitoring changes to the 'orders' query
    const observer = new QueryObserver(queryClient, { queryKey: ['carts'] });

    // Subscribe to the observer to listen for updates to the query
    const unsubscribe = observer.subscribe(async queryResult => {
      const orders = Array.isArray(queryResult.data) ? queryResult.data : [];
      const unReceivedOrders = orders.filter(
        order => !order.order_received_by_vendor_at,
      );
      // Retrieve the last seen order ID from AsyncStorage, defaulting to -1 if not found
      const lastSeenOrderId = +(
        (await AsyncStorage.getItem('lastSeenOrderId')) || '-1'
      );
      // Determine new orders based on lastSeenOrderId
      const newOrders = orders.filter(order => order.id > lastSeenOrderId);

      callbacks.forEach(({ callback }) => callback(newOrders, orders));

      unReceivedOrders.forEach(order => {
        // Set the order as received and print if necessary.
        setOrderReceived.mutateAsync(order.id).then(({ auto_print }) => {
          if (auto_print) {
            printReceipt
              .mutateAsync(`/store/vendor/carts/${order.id}/print_receipt.json`)
              .then(data => printService.print(data.print_jobs));
          }
        });
      });
    });

    return () => {
      unsubscribe(); // Clean up observer subscription on unmount
    };
  }, [callbacks]);

  return (
    <OrdersContext.Provider value={{ registerCallback }}>
      {props.children}
    </OrdersContext.Provider>
  );
};
